import type { AbstractControl, NonNullableFormBuilder, ValidationErrors } from '@angular/forms';
import { Validators } from '@angular/forms';
import { type MixedFormControlsInterface, enumValidator, selectControlValue } from '@comparit/core';
import { map, pairwise, shareReplay, startWith, tap } from 'rxjs';
import type { HausratVorschaden, SachVorversicherung, Step10FormInput } from '../../../interfaces';
import {
  HausratSchadenart,
  JahreBeimVorversicherer,
  KuendigungDurchEnum,
} from '../../../interfaces';
import { SCHADENHOEHE_MAX, SCHADENHOEHE_MIN, vorschadenJahrValidator } from '../vorschaden.helper';

/** A ticked "Schäden" box with an empty list is an unfinished answer, not "keine Schäden". */
function vorschaedenValidator(control: AbstractControl): ValidationErrors | null {
  return Array.isArray(control.value) && control.value.length === 0 ? { required: true } : null;
}

export class Step10Form {
  constructor(
    private fb: NonNullableFormBuilder,
    private defaultValue?: Step10FormInput | null
  ) {
    this.vorversicherungToggle$.subscribe();
    this.schaedenToggle$.subscribe();
    this.untickWhenLastVorschadenRemoved$.subscribe();
  }

  vorversicherungCtrls: MixedFormControlsInterface<SachVorversicherung> = {
    gekuendigtDurch: this.fb.control<KuendigungDurchEnum | null>(
      this.defaultValue?.vorversicherung?.gekuendigtDurch ??
        KuendigungDurchEnum.Versicherungsnehmer,
      [Validators.required, enumValidator(KuendigungDurchEnum)]
    ),
    jahreBeimVorversicherer: this.fb.control<JahreBeimVorversicherer | null>(
      this.defaultValue?.vorversicherung?.jahreBeimVorversicherer ??
        JahreBeimVorversicherer.Bis1Jahr,
      [Validators.required, enumValidator(JahreBeimVorversicherer)]
    ),
    ununterbrochen5Jahre: this.fb.control<boolean | null>(
      this.defaultValue?.vorversicherung?.ununterbrochen5Jahre ?? false
    ),
  };

  private vorschaedenFa = this.fb.array(
    (this.defaultValue?.vorschaeden ?? []).map(vorschaden => this.buildVorschaden(vorschaden)),
    [vorschaedenValidator]
  );

  ctrls: MixedFormControlsInterface<Step10FormInput> = {
    vorversicherung: this.fb.group<MixedFormControlsInterface<SachVorversicherung>>(
      this.vorversicherungCtrls
    ),
    vorschaeden: this.vorschaedenFa,
    // Inside the group so they are persisted, and so the mapper can tell a hidden section from a
    // filled one — `getRawValue()` hands back disabled controls too.
    vorversicherungVorhanden: this.fb.control<boolean>(
      this.defaultValue?.vorversicherungVorhanden ?? true
    ),
    schaedenVorhanden: this.fb.control<boolean>(this.defaultValue?.schaedenVorhanden ?? false),
  };

  public vorversicherungVorhanden$ = selectControlValue(this.ctrls.vorversicherungVorhanden);
  public schaedenVorhanden$ = selectControlValue(this.ctrls.schaedenVorhanden);
  /** `selectControlValue` only takes a FormControl, and the raw value is the DTO shape. */
  public vorschaeden$ = this.ctrls.vorschaeden.valueChanges.pipe(
    startWith(null),
    map(() => this.vorschaeden),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private vorversicherungToggle$ = this.vorversicherungVorhanden$.pipe(
    tap(vorhanden => {
      if (vorhanden) this.ctrls.vorversicherung.enable();
      else this.ctrls.vorversicherung.disable();
    })
  );

  private schaedenToggle$ = this.schaedenVorhanden$.pipe(
    tap(vorhanden => {
      if (vorhanden) this.ctrls.vorschaeden.enable();
      else this.ctrls.vorschaeden.disable();
    })
  );

  /** Removing the last Schaden is how the customer says "doch keine" — honour it. */
  private untickWhenLastVorschadenRemoved$ = this.vorschaeden$.pipe(
    pairwise(),
    tap(([previous, current]) => {
      if (previous.length > 0 && current.length === 0 && this.ctrls.schaedenVorhanden.value) {
        this.ctrls.schaedenVorhanden.setValue(false);
      }
    })
  );

  private buildVorschaden(vorschaden?: HausratVorschaden) {
    const fg = this.fb.group<MixedFormControlsInterface<HausratVorschaden>>({
      art: this.fb.control<HausratSchadenart | null>(vorschaden?.art ?? null, [
        Validators.required,
        enumValidator(HausratSchadenart),
      ]),
      jahr: this.fb.control<number | null>(vorschaden?.jahr ?? null, [
        Validators.required,
        vorschadenJahrValidator,
      ]),
      hoehe: this.fb.control<number | null>(vorschaden?.hoehe ?? null, [
        Validators.required,
        Validators.min(SCHADENHOEHE_MIN),
        Validators.max(SCHADENHOEHE_MAX),
      ]),
    });

    // The Jahr window depends on the sibling Art, which the control cannot see until the group
    // has adopted it — its first run happened without one.
    fg.controls.jahr.updateValueAndValidity();

    return fg;
  }

  get vorschaeden(): HausratVorschaden[] {
    return this.ctrls.vorschaeden.getRawValue();
  }

  addVorschaden(vorschaden: HausratVorschaden): void {
    this.ctrls.vorschaeden.push(this.buildVorschaden(vorschaden));
  }

  updateVorschaden(index: number, vorschaden: HausratVorschaden): void {
    this.ctrls.vorschaeden.setControl(index, this.buildVorschaden(vorschaden));
  }

  removeVorschaden(index: number): void {
    this.ctrls.vorschaeden.removeAt(index);
  }

  /** `_fg.setValue` cannot grow or shrink a FormArray, so a reset has to clear it by hand. */
  reset(): void {
    this.ctrls.vorschaeden.clear();
  }

  public fg = this.fb.group(this.ctrls);
}
